import React, { useState } from 'react';
import { Plus, Minus } from '@phosphor-icons/react';

/**
 * Home page accordion banner.
 *
 *   • Closed by default (unless defaultOpen).
 *   • Entire banner is the click target (header + chevron).
 *   • Right edge shows + when closed, - when open.
 *   • Open content renders directly below, left-aligned beneath the title.
 *   • Optional `variant` gives each home section a subtle visual identity
 *     while keeping layout/behavior identical.
 *
 * Variants: 'primary' | 'challenge' | 'events' | 'news' | 'calendar'
 *
 * Each variant tweaks:
 *   - card background tint
 *   - left accent strip color
 *   - icon tile background + foreground
 *   - toggle pill ring color
 *
 * Usage:
 *   <HomeAccordion title="My Events" subtitle="…" variant="primary" />
 */
const VARIANTS = {
  // RTC green — strongest, used for My Events (primary personal section).
  primary: {
    card: 'bg-white border-[#cfd9d2]',
    accent: 'bg-[#015e3f]',
    iconWrap: 'bg-[#015e3f]/12 text-[#015e3f]',
    toggle: 'border-[#cfd9d2] text-[#015e3f]',
    hover: 'hover:bg-[#015e3f]/[0.04]',
  },
  // Amber/gold — Active Challenges, energetic & action-oriented.
  challenge: {
    card: 'bg-white border-[#e6d4ad]',
    accent: 'bg-[#b8862a]',
    iconWrap: 'bg-[#b8862a]/14 text-[#8a6418]',
    toggle: 'border-[#e6d4ad] text-[#8a6418]',
    hover: 'hover:bg-[#b8862a]/[0.05]',
  },
  // Slate-green — All Events, calm browse/explore feel.
  events: {
    card: 'bg-white border-[#d4ddd7]',
    accent: 'bg-[#5a8a73]',
    iconWrap: 'bg-[#5a8a73]/14 text-[#3f6a56]',
    toggle: 'border-[#d4ddd7] text-[#3f6a56]',
    hover: 'hover:bg-[#5a8a73]/[0.05]',
  },
  // Cream/paper — Club News announcements.
  news: {
    card: 'bg-[#faf5ea] border-[#e6d9bd]',
    accent: 'bg-[#a37a3d]',
    iconWrap: 'bg-[#a37a3d]/15 text-[#7a5a2c]',
    toggle: 'border-[#e6d9bd] text-[#7a5a2c]',
    hover: 'hover:bg-[#f3e8cf]/60',
  },
  // Blue-gray — Calendar, calm/informational.
  calendar: {
    card: 'bg-white border-[#d3dde2]',
    accent: 'bg-[#5d7a8a]',
    iconWrap: 'bg-[#5d7a8a]/14 text-[#3e5666]',
    toggle: 'border-[#d3dde2] text-[#3e5666]',
    hover: 'hover:bg-[#5d7a8a]/[0.05]',
  },
};

export default function HomeAccordion({
  title,
  subtitle,
  icon: Icon,
  testId,
  defaultOpen = false,
  variant = 'primary',
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((v) => !v);
  const v = VARIANTS[variant] || VARIANTS.primary;

  return (
    <section
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
      data-variant={variant}
      className={`relative ${v.card} border rounded-2xl overflow-hidden`}
    >
      {/* Left accent strip — subtle vertical identity bar */}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 bottom-0 w-[3px] ${v.accent}`}
      />
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className={`w-full flex items-center gap-3 px-4 py-4 transition-colors ${v.hover}`}
      >
        {Icon && (
          <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${v.iconWrap}`}>
            <Icon size={18} weight="fill" />
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm sm:text-base font-medium text-[#013523]">
            {title}
          </div>
          {subtitle && (
            <div className="text-xs text-[#6a766e] mt-0.5 truncate">
              {subtitle}
            </div>
          )}
        </div>
        <div
          aria-hidden="true"
          className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center ${v.toggle}`}
        >
          {open ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
        </div>
      </button>
      {open && (
        <div
          data-testid={`${testId}-content`}
          className="px-3 pt-3 pb-3 border-t border-[#e3dcd1]/60"
        >
          {children}
        </div>
      )}
    </section>
  );
}
